/*global _ */
const EventBus = window.VueEventBus

import props from '../re-quick-cart/props'

import CartMixin from '../../../../mixins/cart'
import ProductMixin from '../../../../mixins/store/product'
import CartLocationMixin from '../../../../mixins/cart/location'
import ResponsiveMixin from '../../../../mixins/responsive'
import ComponentMixin from '../../../../mixins/components/complex-reactive'

export default {
  name: 're-quick-cart-menu',
  settings: 'element.quick-cart',
  translations: 'element.quick-cart',
  mixins: [
    ComponentMixin,
    CartMixin,
    ProductMixin,
    CartLocationMixin,
    ResponsiveMixin,
  ],
  props: {
    cartView: {
      type: String,
      default() {
        return this.$options.$$s('cartView', 'modal') // modal, left, right, dropdown
      }
    },
    openOn: {
      type: String,
      default() {
        return this.$options.$$s('openOn', 'click') // click//hover
      }
    },
    openWhen: {
      type: String,
      default() {
        return this.$options.$$s('openWhen', 'first') // first, each
      }
    },
    cartContainerClass: {
      type: [String, Array, Object],
      default() {
        return this.$options.$$s('cartContainerClass', '')
      }
    },
    ...props,
  },
  data() {
    return {
      // applied by mergedAttr (mixins/base.js)
      renameMergeAttrProps: {
        cartContainerClass: 'containerClass',
      },
      // applied by mergedAttr (mixins/base.js)
      omitMergeAttrProps: [
        'cartView',
        'openOn',
        'openWhen',
        'id',
      ],
      quickCartVisible: null
    }
  },
  computed: {
    visibleCartItemsCount() {
      return (_.reject(this.currentCartItems || {}, (i) => i.attributes.skipCartCount)).length
    },
    isOpenOnClick() {
      if (this.isDesignerMode) {
        return false
      }
      return (this.$$getData('openOn') || 'click') === 'click'
    },
    isOpenOnHover() {
      if (this.isDesignerMode) {
        return false
      }
      return this.$$getData('openOn') === 'hover'
    },
    isOpenOnFirstNewCartItem() {
      if (this.isDesignerMode) {
        return false
      }
      return (this.$$getData('openWhen') || 'first') === 'first'
    },
    isOpenOnAnyNewCartItem() {
      if (this.isDesignerMode) {
        return false
      }
      return this.$$getData('openWhen') === 'each'
    },
    isQuickCartVisible() {
      return this.$$getData('quickCartVisible')
    },
    quickCartViewName() {
      return this.$$getData('cartView') || 'modal'
    },
    isModalView() {
      return this.quickCartViewName === 'modal'
    },
    isDropdownView() {
      return this.quickCartViewName === 'dropdown'
    },
    isLeftSidebarView() {
      return this.quickCartViewName === 'left'
    },
    isRightSidebarView() {
      return this.quickCartViewName === 'right'
    },
    isSidebarView() {
      return this.isLeftSidebarView || this.isRightSidebarView
    },
    sideBarStyles() {
      return {
        top: 0,
        bottom: 0,
        left: this.isLeftSidebarView ? 0 : 'auto',
        right: this.isRightSidebarView ? 0 : 'auto',
        'maxHeight': '100vh',
        'minHeight': '100vh'
      }
    },
    sideBarVirtualElement() {
      return {
        getBoundingClientRect: () => {
          return () => ({
            width: 0,
            height: 0,
            top: 0,
            bottom: 0,
            left: this.isLeftSidebarView ? 0 : 'auto',
            right: this.isRightSidebarView ? 0 : 'auto',
          })
        }
      }
    },
    quickCartContainerType() {
      return {
        'modal': 'modal',
        'dropdown': 'popper',
        'left': 'sidebar',
        'right': 'sidebar',
      }[this.quickCartViewName]
    },
    quickCartContainerSubType() {
      return {
        'left': 'sidebar-left',
        'right': 'sidebar-right',
      }[this.quickCartViewName]
    },
    quickCartContainerComponent() {
      return this.isModalView ? 're-modal' : 're-popper'
    },
    quickCartContainerComponentKey() {
      return this.quickCartContainerComponent + '-' + this.quickCartViewName
    },
    quickCartContainerComponentAttrs() {
      const attrs = {
        noClose: false,
        class: this.getClasses('quick-cart', true)
      }
      if (this.isModalView) {
        attrs.closeOnEscape = true
        attrs.containerClass = this.getClasses('quick-cart-view')
      } else {
        attrs.contentClass = this.getClasses('quick-cart-view')
      }
      if (this.isSidebarView) {
        attrs.reference = this.sideBarVirtualElement
        attrs.autoLimitHeight = false
        attrs.popperStyles = this.sideBarStyles
      }
      return attrs
    },
    quickCartAttrs() {
      const attrs = {
        noClose: true
      }
      if (this.isDesignerMode) {
        const conf = this.designerObject
        if (conf) {
          const keys = [
            'showCount',
            'showItemImage'
          ]
          _.forOwn(keys, key => {
            attrs[key] = conf[key]
          })
        }
      }
      return {...this.mergedAttr, ...attrs}
    }
  },
  watch: {
    visibleCartItemsCount(count, oldCount) {
      if (count && count > (oldCount || 0)) {
        if (
          this.isOpenOnAnyNewCartItem ||
          (count === 1 && this.isOpenOnFirstNewCartItem)
        ) {
          this.quickCartVisible = true
        }
      }
    },
    quickCartVisible(v) {
      if (!v) {
        this.removeHoverOutListener()
      }
    }
  },
  methods: {
    getClasses(prefix, skipPrefix) {
      const classes = []
      if (!skipPrefix) {
        classes.push(prefix)
      }
      if (this.quickCartContainerType) {
        classes.push(`${prefix}-${this.quickCartContainerType}`)
      }
      if (this.quickCartContainerSubType) {
        classes.push(`${prefix}-${this.quickCartContainerSubType}`)
      }
      return classes
    },
    mayClickOpenQuickCart() {
      if (this.isOpenOnClick) {
        this.quickCartVisible = !this.quickCartVisible
      }
    },
    mayHoverOpenQuickCart() {
      if (this.isOpenOnHover) {
        this.quickCartVisible = true
        if (this.isDropdownView) {
          this.addHoverOutListener()
        }
      }
    },
    addHoverOutListener() {
      const el = this.$el
      const document = el.ownerDocument
      document.body.addEventListener('mouseover', this.onHoverOut)
    },
    removeHoverOutListener() {
      const el = this.$el
      const document = el.ownerDocument
      document.body.removeEventListener('mouseover', this.onHoverOut)
    },
    onHoverOut(event) {
      const el = this.$el
      const ref = this.$refs.quickCart
      const refEl = ref && ref.$el
      const target = event.target
      const ownEl = el === target || el.contains(target) ||
        (refEl && (refEl === target || refEl.contains(target)))
      if (!ownEl) {
        this.quickCartVisible = false
      }
    },
    closeQuickCart() {
      this.quickCartVisible = false
    },
  }
}
